import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  PaperPlaneTilt,
  CheckCircle,
  XCircle,
  Clock,
  Plugs,
  ArrowRight,
  WarningCircle,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [cooldown, setCooldown] = useState(null);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch((e) => console.error("stats failed", e));
    api.get("/history", { params: { limit: 8 } }).then((r) => setRecent(r.data)).catch((e) => console.error("history failed", e));
    api.get("/mysaas/status")
      .then((r) => setCooldown(r.data?.rate_limited ? r.data : null))
      .catch((e) => console.error("mysaas status failed", e));
  }, []);

  return (
    <div className="p-8 lg:p-12 max-w-[1400px]">
      <header className="flex items-end justify-between mb-10">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-2">Overview</div>
          <h1 className="font-display text-5xl font-black tracking-tight" data-testid="dashboard-title">Command deck</h1>
        </div>
        <Link
          to="/connections/new"
          data-testid="dashboard-new-connection"
          className="inline-flex items-center gap-2 bg-[#F58021] hover:bg-[#D6691A] text-white px-5 h-11 rounded-md font-semibold transition-colors"
        >
          New connection <ArrowRight weight="bold" size={16} />
        </Link>
      </header>

      {cooldown && (
        <div className="mb-6 border border-amber-300 bg-amber-50 p-4 flex items-start gap-3" data-testid="rate-limit-banner">
          <WarningCircle weight="duotone" size={22} className="text-amber-700 mt-0.5" />
          <div>
            <div className="font-semibold text-amber-900">MySaaS rate limit reached</div>
            <div className="text-sm text-amber-800">
              MySaaS is rate-limiting requests. Polling and retries are paused for about {Math.max(1, Math.round(cooldown.seconds_remaining / 60))} minute{cooldown.seconds_remaining > 60 ? "s" : ""}. Skipped rows are held safely and will retry automatically once the limit clears.
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
        <MetricCard
          testid="stat-total"
          label="Total sent"
          value={stats?.total ?? "—"}
          icon={<PaperPlaneTilt weight="duotone" size={22} />}
          highlight
        />
        <MetricCard testid="stat-success" label="Delivered" value={stats?.success ?? "—"} icon={<CheckCircle weight="duotone" size={22} className="text-emerald-700" />} />
        <MetricCard testid="stat-failed" label="Failed" value={stats?.failed ?? "—"} icon={<XCircle weight="duotone" size={22} className="text-red-700" />} />
        <MetricCard testid="stat-skipped" label="Skipped" value={stats?.skipped ?? "—"} icon={<Clock weight="duotone" size={22} className="text-neutral-500" />} />
        <MetricCard testid="stat-active" label="Active sheets" value={`${stats?.active_connections ?? "—"}/${stats?.total_connections ?? "—"}`} icon={<Plugs weight="duotone" size={22} />} />
      </div>

      <section className="bg-white border border-stone-200">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200">
          <h2 className="font-display text-xl font-bold tracking-tight">Recent activity</h2>
          <Link to="/history" className="text-sm text-neutral-600 hover:text-black">View all →</Link>
        </div>

        {recent.length === 0 ? (
          <EmptyState />
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-neutral-500 border-b border-stone-200">
              <tr>
                <th className="text-left py-3 px-6">When</th>
                <th className="text-left py-3 px-3">Connection</th>
                <th className="text-left py-3 px-3">Recipient</th>
                <th className="text-left py-3 px-3">Status</th>
              </tr>
            </thead>
            <tbody data-testid="recent-activity-body">
              {recent.map((h) => (
                <tr key={h.entry_id} className="border-b border-stone-100 last:border-0 hover:bg-stone-50">
                  <td className="py-3 px-6 font-mono-tech text-xs text-neutral-600">
                    {new Date(h.created_at).toLocaleString()}
                  </td>
                  <td className="py-3 px-3 font-medium">{h.connection_label}</td>
                  <td className="py-3 px-3">
                    <div className="font-medium">{h.recipient_name || "—"}</div>
                    <div className="text-xs text-neutral-500 font-mono-tech">{h.recipient_email || h.recipient_phone}</div>
                  </td>
                  <td className="py-3 px-3"><StatusBadge status={h.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function MetricCard({ label, value, icon, highlight, testid }) {
  return (
    <div
      data-testid={testid}
      className={`bg-white border border-stone-200 p-6 transition-transform hover:-translate-y-0.5 ${highlight ? "lg:col-span-1" : ""}`}
    >
      <div className="flex items-center justify-between text-neutral-500 mb-4">
        <span className="text-xs uppercase tracking-widest">{label}</span>
        <span>{icon}</span>
      </div>
      <div className={`font-display font-black tracking-tight ${highlight ? "text-5xl text-[#F58021]" : "text-4xl text-neutral-900"}`}>
        {value}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="p-12 text-center">
      <Clock weight="duotone" size={40} className="mx-auto text-neutral-400 mb-3" />
      <div className="font-display text-xl font-bold mb-2">No activity yet</div>
      <p className="text-neutral-500 text-sm mb-4">Connect a Google Sheet to start sending review requests automatically when rows are added.</p>
      <Link to="/connections/new" className="inline-flex items-center gap-2 border border-neutral-900 px-4 h-10 text-sm font-semibold hover:bg-neutral-900 hover:text-white transition-colors">
        Add your first connection <ArrowRight weight="bold" size={14} />
      </Link>
    </div>
  );
}
